import { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { usePreviewContactsQuery } from 'features/crm/previewModal/usePreviewContactsQuery';
import { CrmExportPreviewAccountSelect } from 'pages/contacts/ContactItem/CrmExportPreviewModal/CrmExportPreviewAccountSelect';

import type { Contact } from '../../../../domain/contact';
import type { CrmAccount } from '../../../../domain/crm';
import { Crm } from '../../../../domain/crm';
import { useCrmStatus } from '../../../../hooks/useCrmStatus';
import { StyledLoader } from '../../ContactList/styles';
import { AccountOwnerTooltip } from '../AccountOwnerTooltip/AccountOwnerTooltip';

import { StyledCrmExportPreviewLoader, StyledCrmExportPreviewModalText, StyledModal } from './styles';

export type CrmExportPreviewModalProps = {
  crm: Crm;
  contact: Contact;
  isOpen: boolean;
  onClose(): void;
  onConfirm(selectedAccount?: CrmAccount | null): void;
  selectedAccount: CrmAccount | null;
  setSelectedAccount: (account: CrmAccount) => void;
};

export const CrmExportPreviewModal = ({
  crm,
  contact,
  isOpen,
  onClose,
  onConfirm,
  selectedAccount,
  setSelectedAccount,
}: CrmExportPreviewModalProps) => {
  const { t } = useTranslation();

  const contacts = useMemo(() => [contact], [contact]);
  const { data, isLoading } = usePreviewContactsQuery({ crm, contacts, enabled: isOpen });

  const { isSfLabelsEnabled, isHbLabelsEnabled } = useCrmStatus();
  const isCrmLabelsEnabled = crm === Crm.salesforce ? isSfLabelsEnabled : isHbLabelsEnabled;

  useEffect(() => {
    if (!isOpen || !data) {
      return;
    }
    const shouldExport = data[0].matchedCompanies.length === 1;

    if (shouldExport) {
      setSelectedAccount(data[0].matchedCompanies[0]);
      onConfirm(data[0].matchedCompanies[0]);
    }
  }, [data, isOpen]);

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      heading={
        crm === Crm.salesforce
          ? t('contacts.contact.export_to_crm.preview_modal.heading.sf')
          : t('contacts.contact.export_to_crm.preview_modal.heading.hs')
      }
      footerJustifyContent="flex-end"
      confirmButtonProps={{
        children: t('contact_card.export_crm'),
        onClick: () => onConfirm(),
        disabled: !selectedAccount,
        variant: 'primary',
      }}
      data-automation="CrmExportPreviewModal"
    >
      <StyledCrmExportPreviewModalText>
        {t('contacts.contact.export_to_crm.preview_modal.title', { name: contact.name })}
      </StyledCrmExportPreviewModalText>
      {isCrmLabelsEnabled ? null : <AccountOwnerTooltip crm={crm} withPadding={false} />}
      {isLoading ? (
        <StyledCrmExportPreviewLoader>
          <StyledLoader />
        </StyledCrmExportPreviewLoader>
      ) : (
        <CrmExportPreviewAccountSelect
          crm={crm}
          companyName={contact.companyName}
          isOpen={isOpen}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          matchedCompanies={data?.[0].matchedCompanies || []}
        />
      )}
    </StyledModal>
  );
};
